import { type ReactNode, useSyncExternalStore } from 'react';
import { useAuthStore } from '@/stores/auth.store';

/**
 * Blocks the route tree from rendering until the persisted auth store
 * (tokens + user, in localStorage) has rehydrated. Without this, the
 * router's `RequireAuth` guard would see `accessToken: null` for a frame
 * on hard reload and bounce an already-authenticated user to /login.
 */
export function AuthHydrationGate({ children }: { children: ReactNode }) {
  const isHydrated = useSyncExternalStore(
    useAuthStore.subscribe,
    () => useAuthStore.getState().isHydrated,
    () => useAuthStore.getState().isHydrated,
  );

  // Persisted store with no storage entry yet (first-ever visit) never
  // fires onRehydrateStorage with a truthy state in some edge cases —
  // guard with the store's own hasHydrated() as a fallback.
  if (!isHydrated && !useAuthStore.persist.hasHydrated()) {
    return null;
  }

  return <>{children}</>;
}
